import { Vue, Component, Ref, Prop, Emit } from 'vue-property-decorator'
import { Nullable } from '@/shared/types/app.types'
import { initialLocalTargetResponseTimeModalState, LocalTargetResponseTimeComboParsing, LocalTargetResponseTimeModalComboElement, LocalTargetResponseTimeModalState } from '@/modules/configurationModule/detail/local-samples/target-response-times/components/target-response-times-modal/target-response-times-modal.model'
import { ConfigurationTargetResponseTimeConfigurationCreateRequest, ConfigurationTargetResponseTimeConfigurationPatchInBatchRequest, ConfigurationTargetResponseTimeConfigurationResponse } from '@/modules/configurationModule/detail/local-samples/target-response-times/services/target-response-types.dto'
import { commonPriorityService } from '@/api/services/common/priority/common-priority.service'
import { CommonPriorityResponse } from '@/api/services/common/priority/common-priority.dto'
import { commonDepartmentService } from '@/api/services/common/department/common-department.service'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { CommonSampleTypeResponse } from '@/api/services/common/sample-type/common-sample-type.dto'
import { CommonTopographicCodeResponse } from '@/api/services/common/topographic-code/common-topographic-code.dto'
import { CommonProcedureResponse } from '@/api/services/common/procedure/common-procedure.dto'
import { CommonMostLikelyMorphologicCodeResponse } from '@/api/services/common/most-likely-morphologic-code/common-most-likely-morphologic-code.dto'
import { commonCenterService } from '@/api/services/common/center/common-center.service'
import { CommonCenterResponse } from '@/api/services/common/center/common-center.dto'

@Component
export default class TargetResponseTimesModal extends Vue {
    @Ref() readonly localTargetResponseTimeModal!: any

    @Prop(String) readonly actionTitle!: string
    @Prop(Number) readonly laboratoryId!: number

    @Emit()
    update(localSampleTypePatch: ConfigurationTargetResponseTimeConfigurationPatchInBatchRequest[]): ConfigurationTargetResponseTimeConfigurationPatchInBatchRequest[] {
        return localSampleTypePatch
    }

    @Emit()
    create(localSampleTypeCreate: ConfigurationTargetResponseTimeConfigurationCreateRequest): ConfigurationTargetResponseTimeConfigurationCreateRequest {
        return localSampleTypeCreate
    }

    state: LocalTargetResponseTimeModalState
    constructor() {
        super()
        this.state = initialLocalTargetResponseTimeModalState()
    }

    get canSelectTopographicCode(): boolean {
        return !!this.state.sampleTypes.selected?.id
    }

    get canSelectProcedure(): boolean {
        return !!this.state.topographicCodes.selected?.id
    }

    get canSelectMostLikelyMorphologicCodes(): boolean {
        return !!this.state.procedures.selected?.id
    }

    get canSelectedDepartment(): boolean {
        return !!this.state.centers.selected?.id
    }

    get canUpdateCombo(): boolean {
        return !this.state?.id
    }

    get canUpdate(): boolean {
        const hasError: boolean = !!this.errors.count()
        const anyFieldIsDirty: boolean = this.$_someFieldIsDirty(this.fields)
        const comboSelectionValid: boolean = !!this.state.sampleTypes.selected?.id
        return !hasError && anyFieldIsDirty && comboSelectionValid
    }

    async show(selectedRow: Nullable<ConfigurationTargetResponseTimeConfigurationResponse>): Promise<void> {
        this.state = initialLocalTargetResponseTimeModalState()
        await this._fillAllCombos()
        await this.$validator.reset()
        if (selectedRow) {
            this.updateRow(selectedRow)
            await this._setSelectionCombo(selectedRow)
        }
        this._showModal()
    }

    hide(): void { this.localTargetResponseTimeModal.hide() }

    updateRow(selectedRow: Nullable<ConfigurationTargetResponseTimeConfigurationResponse>): void {
        this._setSelectedRow(selectedRow)
    }

    spreadEvent(): void {
        if (this.state?.id) {
            const patchRow: ConfigurationTargetResponseTimeConfigurationPatchInBatchRequest = {
                id: this.state.id,
                daysToDiagnosticTargetResponseDate: this.state.daysToDiagnosticTargetResponseDate,
                daysToGlobalTargetResponseDate: this.state.daysToGlobalTargetResponseDate,
                daysToRegisterTargetResponseDate: this.state.daysToRegisterTargetResponseDate,
                daysToSampleGrossingDelay: this.state.daysToSampleGrossingDelay
            }
            this.update([patchRow])
            return
        }
        const postRow: ConfigurationTargetResponseTimeConfigurationCreateRequest = {
            sampleTypeId: this.state.sampleTypes.selected?.id!,
            topographicCodeId: this.state.topographicCodes.selected?.id!,
            mostLikelyMorphologicCodeId: this.state.mostLikelyMorphologicCodes.selected?.id,
            daysToDiagnosticTargetResponseDate: this.state.daysToDiagnosticTargetResponseDate,
            daysToGlobalTargetResponseDate: this.state.daysToGlobalTargetResponseDate,
            daysToRegisterTargetResponseDate: this.state.daysToRegisterTargetResponseDate,
            daysToSampleGrossingDelay: this.state.daysToSampleGrossingDelay,
            departmentId: this.state.departments.selected?.id,
            priorityId: this.state.priorities.selected?.id,
            procedureId: this.state.procedures.selected?.id
        }
        this.create(postRow)
    }

    async changeCenter(selection: LocalTargetResponseTimeModalComboElement): Promise<void> {
        await this._getDepartments(selection)
        this.state.departments.selected = null
    }

    private _setSelectedRow(selectedRow: Nullable<ConfigurationTargetResponseTimeConfigurationResponse>): void {
        this.state = {
            ...this.state,
            id: selectedRow?.id,
            daysToDiagnosticTargetResponseDate: selectedRow?.daysToDiagnosticTargetResponseDate!,
            daysToGlobalTargetResponseDate: selectedRow?.daysToGlobalTargetResponseDate!,
            daysToRegisterTargetResponseDate: selectedRow?.daysToRegisterTargetResponseDate!,
            daysToSampleGrossingDelay: selectedRow?.daysToSampleGrossingDelay!
        }
    }

    private async _getCommonSampleTypes(): Promise<void> {
        const [err, response] = await this.$_toCall(commonSampleClassificationService.getCommonSampleTypeSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (err) {
            this.state.sampleTypes.list = []
            return
        }
        this.state.sampleTypes.list = this._parseCombo<CommonSampleTypeResponse>(response)
    }

    private async _getCommonTopographicCodes(): Promise<void> {
        const [err, response] = await this.$_toCall(commonSampleClassificationService.getCommonTopographicCodesSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (err) {
            this.state.topographicCodes.list = []
            return
        }
        this.state.topographicCodes.list = this._parseCombo<CommonTopographicCodeResponse>(response)
    }

    private async _getCommonProcedures(): Promise<void> {
        const [err, response] = await this.$_toCall(commonSampleClassificationService.getCommonProceduresSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (err) {
            this.state.procedures.list = []
            return
        }
        this.state.procedures.list = this._parseCombo<CommonProcedureResponse>(response)
    }

    private async _getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory(): Promise<void> {
        const [err, response] = await this.$_toCall(commonSampleClassificationService.getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory({ laboratoryId: this.laboratoryId }))
        if (err) {
            this.state.mostLikelyMorphologicCodes.list = []
            return
        }
        this.state.mostLikelyMorphologicCodes.list = this._parseCombo<CommonMostLikelyMorphologicCodeResponse>(response)
    }

    private async _getPriorities(): Promise<void> {
        const [err, response] = await this.$_toCall(commonPriorityService.getPriorities())
        if (err) {
            this.state.priorities.list = []
            return
        }
        this.state.priorities.list = response.map((priority:CommonPriorityResponse) => ({ id: priority.id, text: priority.name }))
    }

    private async _getCenters(): Promise<void> {
        const [err, response] = await this.$_toCall(commonCenterService.getActiveCenters())
        if (err) {
            this.state.centers.list = []
            return
        }
        this.state.centers.list = response.map((center: CommonCenterResponse) => ({ id: center.id, text: center.name }))
    }

    private async _getDepartments(seletion: LocalTargetResponseTimeModalComboElement): Promise<void> {
        const [err, response] = await this.$_toCall(commonDepartmentService.findAllActiveByCenter(seletion.id))
        if (err) {
            this.state.departments.list = []
            return
        }
        this.state.departments.list = response.map((center: CommonCenterResponse) => ({ id: center.id, text: center.name }))
    }

    private async _setSelectionCombo(selectedRow: Nullable<ConfigurationTargetResponseTimeConfigurationResponse>): Promise<void> {
        this.state.sampleTypes.selected = selectedRow?.sampleType ? { id: selectedRow?.sampleType?.id, text: selectedRow?.sampleType?.description } : null
        this.state.topographicCodes.selected = selectedRow?.topographicCode ? { id: selectedRow?.topographicCode?.id, text: selectedRow?.topographicCode?.description } : null
        this.state.procedures.selected = selectedRow?.procedure ? { id: selectedRow?.procedure?.id, text: selectedRow?.procedure?.description } : null
        this.state.mostLikelyMorphologicCodes.selected = selectedRow?.mostLikelyMorphologicCode ? { id: selectedRow?.mostLikelyMorphologicCode?.id, text: selectedRow?.mostLikelyMorphologicCode?.description } : null
        this.state.priorities.selected = selectedRow?.priority ? { id: selectedRow?.priority?.id, text: selectedRow?.priority?.name } : null
        this.state.centers.selected = selectedRow?.department?.center ? { id: selectedRow?.department?.center.id, text: selectedRow?.department?.center.name } : null
        if (this.state.centers.selected?.id) {
            await this._getDepartments(this.state.centers.selected)
            this.state.departments.selected = selectedRow?.department ? { id: selectedRow?.department?.id, text: selectedRow?.department?.name } : null
        }
    }

    private _showModal(): void {
        this.localTargetResponseTimeModal.show()
    }

    private _parseCombo<T extends LocalTargetResponseTimeComboParsing>(data: T[]): LocalTargetResponseTimeModalComboElement[] {
        return data?.map((itemData: T) => {
            const { id, description: text } = itemData
            return { id, text }
        })
    }

    private async _fillAllCombos(): Promise<void> {
        await Promise.all([
            this._getCommonSampleTypes(),
            this._getCommonTopographicCodes(),
            this._getCommonProcedures(),
            this._getCommonMostLikelyMorphologicCodesSampleClassificationsByLaboratory(),
            this._getPriorities(),
            this._getCenters()
        ])
    }
}
